import { ReactNode, useCallback, useState } from 'react';

import { cx } from '@libs/classnames';
import { Button } from '@ui/components/Button';
import { Card } from '@ui/components/Card';
import { ModalCard } from '@ui/components/ModalCard/ModalCard';
import { PageSubTitle } from '@ui/components/PageSubTitle';

import { TopPlayersList } from '@features/club/ui/TopPlayersList/TopPlayersList';
import { WinnersList } from '@features/club/ui/WinnersList/WinnersList';
import { ClockControlType, TopPlayer, Winner } from '@features/shared/api/typings';

import styles from './ClubParticipants.module.css';

type Props = {
  className?: string;
  groupTitle?: string | null;
  topPlayers?: TopPlayer[];
  onLoadMoreTopPlayers: () => void;
  hasMoreTopPlayers: boolean;
  isTopPlayersLoading: boolean;
  self?: TopPlayer;
  winners?: Winner[];
  onLoadMoreWinners: () => void;
  hasMoreWinners: boolean;
  isWinnersLoading: boolean;
  currentUserId?: string;
  clockControlType: ClockControlType;
  onClockControlTypeChange: (newClockControl: ClockControlType) => void;
  query?: string;
  onQueryChange?: (newQuery: string) => void;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};
export function ClubParticipants({
  className,
  groupTitle,
  topPlayers,
  onLoadMoreTopPlayers,
  hasMoreTopPlayers,
  isTopPlayersLoading,
  self,
  currentUserId,
  winners,
  onLoadMoreWinners,
  hasMoreWinners,
  isWinnersLoading,
  clockControlType,
  onClockControlTypeChange,
  query,
  onQueryChange,
  renderShieldQuickPeek,
}: Props) {
  const [initialClockControlType, setInitialClockControlType] =
    useState<ClockControlType>(clockControlType);
  const [topPlayersModalVisible, setTopPlayersModalVisible] = useState(false);
  const [winnersModalVisible, setWinnersModalVisible] = useState(false);

  const onTopPlayersModalOpen = useCallback(() => {
    setInitialClockControlType(clockControlType);
    setTopPlayersModalVisible(true);
  }, [setInitialClockControlType, clockControlType, setTopPlayersModalVisible]);
  const onTopPlayersModalClose = useCallback(() => {
    // reset settings
    onQueryChange?.('');
    onClockControlTypeChange(initialClockControlType);
    setTopPlayersModalVisible(false);
  }, [onQueryChange, onClockControlTypeChange, initialClockControlType, setTopPlayersModalVisible]);

  let topPlayersInlineCount = groupTitle ? 5 : 7;
  if (self) topPlayersInlineCount = topPlayersInlineCount - 1;

  return (
    <div className={cx(styles.participants, className)}>
      <PageSubTitle variant="new">Участники</PageSubTitle>
      <div className={styles.cards}>
        <Card flat className={styles.topPlayers}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Топ игроков</div>
            {topPlayers && topPlayers.length > 5 && (
              <Button variant="slim" onClick={onTopPlayersModalOpen}>
                Смотреть всех ›
              </Button>
            )}
          </div>

          <TopPlayersList
            groupTitle={groupTitle}
            topPlayers={topPlayers?.slice(0, topPlayersInlineCount)}
            self={self}
            clockControlType={clockControlType}
            onClockControlTypeChange={onClockControlTypeChange}
            currentUserId={currentUserId}
            hasMore={false}
            onLoadMore={onLoadMoreTopPlayers}
            isLoading={isTopPlayersLoading}
            renderShieldQuickPeek={renderShieldQuickPeek}
          />

          {topPlayersModalVisible && (
            <ModalCard title="Топ игроков" onClose={onTopPlayersModalClose}>
              <TopPlayersList
                listClassName={styles.topPlayersModal}
                groupTitle={groupTitle}
                topPlayers={topPlayers}
                self={self}
                clockControlType={clockControlType}
                onClockControlTypeChange={onClockControlTypeChange}
                currentUserId={currentUserId}
                query={query}
                onQueryChange={onQueryChange}
                onLoadMore={onLoadMoreTopPlayers}
                hasMore={hasMoreTopPlayers}
                hoverable
                isLoading={isTopPlayersLoading}
                renderShieldQuickPeek={renderShieldQuickPeek}
              />
            </ModalCard>
          )}
        </Card>

        <Card flat className={styles.winners}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Победители турниров</div>
            {winners && winners.length > 3 && (
              <Button variant="slim" onClick={() => setWinnersModalVisible(true)}>
                Смотреть всех ›
              </Button>
            )}
          </div>

          <WinnersList
            className={styles.winnersList}
            winners={winners?.slice(0, 9)}
            maxVisible={9}
            onLoadMore={onLoadMoreWinners}
            hasMore={false}
            isLoading={isWinnersLoading}
          />

          {winnersModalVisible && (
            <ModalCard title="Победители турниров" onClose={() => setWinnersModalVisible(false)}>
              <WinnersList
                className={styles.winnersModal}
                winners={winners}
                onLoadMore={onLoadMoreWinners}
                hasMore={hasMoreWinners}
                isModal
                isLoading={isWinnersLoading}
              />
            </ModalCard>
          )}
        </Card>
      </div>
    </div>
  );
}
